import React from 'react'
import Immutable from 'immutable';
import Icon from '../icon';
import styled from 'styled-components';
import { defaultTheme } from '../../data/themes';


const EMPTY_LIST = Immutable.List([]);
const EMPTY_SET = Immutable.Set([]);

const TreeWrapper = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	overflow: auto;

	padding: ${props => props.theme.treePadding || defaultTheme.treePadding};
	background: ${props => props.theme.treeBackground || defaultTheme.treeBackground};
`;

const NodeContainer = styled.div`
	&.root {
		&:not(:last-child) {
			border-bottom: 1px solid ${props => props.theme.altBorderColor || '#43434327'};
		}
		padding: 8px 0px;
	}
`;

const DummyIcon = styled.span`
	display: inline-block;
	width: 12px;
	margin-right: 4px;
`;

const NodeText = styled.span`
	font-weight: ${props => props.theme.treeFontWeight || defaultTheme.treeFontWeight};

	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}

`;

const Children = styled.div`
	padding-left: ${props => props.theme.treeChildOffset || defaultTheme.treeChildOffset};
`;


const Node = styled.div`
	white-space: nowrap;
	.icon {
		position: relative;
		top: -2px;
		margin-right: 4px;

		&.checkbox {
			font-size: 15px;
		}

		&[data-name=checked], &[data-name=checkedParent] {
			color: ${props => props.theme.checkedIconColor || defaultTheme.checkedIconColor};
		}


		&[data-name=expanded], &[data-name=collapsed] {
			top: -3px;
			min-width: 12px;
			text-align: center;
		}
	}

	&.checked, &.checkedParent {
		.nodeText {
			font-weight: ${props => props.theme.treeSelectedFontWeight || defaultTheme.treeSelectedFontWeight};
			color: ${props => props.theme.treeSelectedTextColor || defaultTheme.treeSelectedTextColor};
		}
	}

`;


function getNodeStructure(nodes) {
	if (!nodes) {
		return null;
	}
	let res = {
		selectableChildren: {},
		selectableChildMap: {},
		nodes: nodes.toJS()
	};

	const processParent = function(node) {
		const nodes = node.nodes;
		if (!nodes) {
			return [];
		}
		const name = node.name;
		let selectableChildren = [];
		nodes.forEach(node => {
			if (node.nodes) {
				processParent(node).forEach(sc => {
					selectableChildren.push(sc);
				});
			} else {
				selectableChildren.push(node.name);
			}
		});
		res.selectableChildren[name] = selectableChildren;
		let map = {};
		selectableChildren.forEach(sc => map[sc] = true);
		res.selectableChildMap[name] = map;
		return selectableChildren.slice(0);
	};

	res.nodes.forEach(node => processParent(node));
	return res;
}

function getParentSelectionStates(nodeStructure, selected) {
	let res = {};
	const getState = function(node) {
		if (node.nodes) {
			let mask = 0;
			node.nodes.forEach(node => {
				mask |= getState(node);
			});
			res[node.name] = (mask === 1 ? 'checkedParent' : (mask === 2 ? 'unchecked' : 'partiallyChecked'));
			return mask;
		} else {
			return selected.has(node.name) ? 1 : 2;
		}
	};
	nodeStructure.nodes.forEach(node => {
		getState(node);
	});
	return res;
}

export default class Tree extends React.PureComponent {
	constructor(props) {
		super();
		const nodeStructure = getNodeStructure(props.nodes);
		const selected = props.selected || EMPTY_SET;
		this.state = {
			nodeStructure,
			nodes: props.nodes,
			parentSelectionStates: getParentSelectionStates(nodeStructure, selected),
			selected
		}
	}

	static getDerivedStateFromProps(nextProps, prevState) {
		let res = null;
		if (nextProps.nodes !== prevState.nodes) {
			const nodeStructure = getNodeStructure(nextProps.nodes);
			res = {
				nodes: nextProps.nodes,
				nodeStructure,
				selected: nextProps.selected,
				parentSelectionStates: getParentSelectionStates(nodeStructure, nextProps.selected)
			};
		} else if (nextProps.selected !== prevState.selected) {
			res = {
				selected: nextProps.selected,
				parentSelectionStates: getParentSelectionStates(prevState.nodeStructure, nextProps.selected)
			};
		}
		return res;
	}

	isSelected(nodeName) {
		return (this.props.selected || EMPTY_SET).has(nodeName);
	}

	isExpanded(nodeName) {
		return (this.props.expanded || EMPTY_SET).has(nodeName);
	}

	setExpanded(nodeName, value) {
		if (this.props.onExpandedChanged) {
			let expanded = this.props.expanded || EMPTY_SET;
			if (value) {
				expanded = expanded.add(nodeName);
			} else {
				expanded = expanded.delete(nodeName);
			}
			this.props.onExpandedChanged(expanded);
		}
	}

	onSelectedChanged(selected) {
		if (this.props.onSelectedChanged) {
			this.props.onSelectedChanged(selected);
		}
	}

	setSelected(node, value) {
		let selected = this.props.selected || EMPTY_SET;
		const name = node.get('name');
		if (!node.get('nodes')) {
			if (value) {
				selected = selected.add(name);
				if (node.get('checkAlong')) {
					selected = selected.add(node.get('checkAlong'));
				}
			} else {
				selected = selected.delete(name);
			}
		}
		const nodeStructure = this.state.nodeStructure;
		if (nodeStructure) {
			if (value) {
				selected = selected.union(nodeStructure.selectableChildren[name] || []);
			} else {
				const map = nodeStructure.selectableChildMap[name] || {};
				selected = selected.filter(sel => !map[sel]);
			}
		}
		this.onSelectedChanged(selected);
	}

	getSelectionState(nodeName) {
		if (this.isSelected(nodeName)) {
			return 'checked';
		}
		return this.state.parentSelectionStates[nodeName] || 'unchecked';
	}

	renderNode(node, index, isRoot) {
		const nodes = node.get('nodes');
		const name = node.get('name');
		const isExpanded = this.isExpanded(name);
		const selectionState = this.getSelectionState(name);
		return (
			<NodeContainer key={index} className={isRoot ? 'root' : ''}>
				<Node className={selectionState}>
					{
						nodes ? (
							<Icon
								name={isExpanded ? 'expanded' : 'collapsed'}
								onMouseDown={e => {
									e.preventDefault();
									this.setExpanded(name, !isExpanded);
								}}
								active
							/>
						) : (
							<DummyIcon/>
						)
					}
					<Icon
						name={selectionState}
						className="checkbox"
						active
						onMouseDown={e => {
							e.preventDefault();
							this.setSelected(node, selectionState === 'unchecked');
						}}
					/>
					<NodeText
						className="nodeText"
						onMouseDown={e => {
							if (e.defaultPrevented) {
								return;
							}
							e.preventDefault();
							if (nodes) {
								this.setExpanded(name, !isExpanded);
							} else {
								this.setSelected(node, selectionState === 'unchecked');
							}
						}}
						title={node.get('text')}
					>
						{node.get('text')}
					</NodeText>
				</Node>
				{
					nodes && isExpanded && (
						<Children>
							{
								nodes.map((child, childIndex) => this.renderNode(child, childIndex))
							}
						</Children>
					)
				}
			</NodeContainer>
		);
	}

	render() {
		if (!this.state.nodes) {
			return null;
		}
		return  (
			<TreeWrapper>
				{
					(this.props.nodes || EMPTY_LIST).map((node, index) => this.renderNode(node, index, true))
				}
			</TreeWrapper>
		);
	}
}
